import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageSquare, 
  Star,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

export default function ReviewsStep({ programmeId, programme, token, headers }) {
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (programmeId) {
      fetchReviews();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/reviews`, { headers });
      setReviewsData(res.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating 
                ? 'text-amber-400 fill-amber-400' 
                : 'text-zinc-600'
            }`}
          />
        ))}
      </div>
    );
  };

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to view reviews</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  const reviews = reviewsData?.reviews_by_activity || [];

  return (
    <div className="space-y-6">
      <div className="p-4 bg-pink-500/10 rounded-lg border border-pink-500/30">
        <p className="text-sm text-pink-300">
          View feedback and ratings submitted by learners for each activity. 
          Use this feedback to improve course content and delivery.
        </p>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
          <MessageSquare className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No reviews yet</h3>
          <p className="text-zinc-400">
            Learner feedback will appear here once they complete activities
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((activityReviews) => (
            <Card key={activityReviews.activity_id} className="bg-zinc-800/50 border-zinc-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-white">Activity: {activityReviews.activity_id}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      {renderStars(Math.round(activityReviews.average_rating))}
                      <span className="text-sm text-zinc-400">
                        {activityReviews.average_rating} average ({activityReviews.total_reviews} reviews)
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-pink-500/20 text-pink-400">
                    {activityReviews.total_reviews} Reviews
                  </Badge>
                </div>

                <div className="space-y-3">
                  {activityReviews.reviews.map((review) => (
                    <div 
                      key={review.review_id}
                      className="p-3 bg-zinc-800 rounded-lg border border-zinc-700"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-300">{review.user_email}</span>
                          {renderStars(review.rating)}
                        </div>
                        <span className="text-xs text-zinc-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {review.feedback && (
                        <p className="text-sm text-zinc-400">{review.feedback}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
